// pages/NocDashboard.jsx
// Admin dashboard for NOC (No Objection Certificate) delivery — lists every
// exhibitor whose security deposit is completed, shows their NOC number and
// delivery status, and lets you send/resend to one or many at once.

import { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import {
  FiFileText, FiCheckCircle, FiClock, FiXCircle, FiRefreshCw,
  FiCopy, FiSend, FiSearch,
} from 'react-icons/fi';

const fmt   = (n)  => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDt = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

const NOC_STATUS = {
  sent:    { bg: 'rgba(0,214,143,.12)',   color: '#00a86b', label: '✅ Sent' },
  pending: { bg: 'rgba(176,176,192,.15)', color: '#6b6b80', label: '— Not sent' },
  failed:  { bg: 'rgba(255,71,87,.12)',   color: '#c0392b', label: '❌ Failed' },
};

function NocChip({ status }) {
  const s = NOC_STATUS[status] || NOC_STATUS.pending;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:14, padding:'18px 22px', display:'flex', alignItems:'center', gap:16 }}>
      <div style={{ width:46, height:46, borderRadius:12, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, color, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:22, fontWeight:800, color:'#1a1a2e', lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:12, color:'#6b6b80', marginTop:3 }}>{label}</div>
      </div>
    </div>
  );
}

export default function NocDashboard() {
  const [exhibitors, setExhibitors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState('People Matters TechHR India 2026');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL'); // ALL | sent | pending | failed

  const [selected, setSelected] = useState(new Set());
  const [sendingIds, setSendingIds] = useState(new Set()); // per-row in-flight state
  const [bulkSending, setBulkSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/noc/exhibitors', { params: eventName ? { eventName } : {} });
      if (!data.success) throw new Error(data.message);
      setExhibitors(data.exhibitors || []);
      setStats(data.stats);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load NOC list');
    } finally {
      setLoading(false);
    }
  }, [eventName]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return exhibitors.filter(e => {
      const statusKey = e.noc_send_status || 'pending';
      const okFilter = filter === 'ALL' || statusKey === filter;
      const okSearch = !q ||
        e.company_name?.toLowerCase().includes(q) ||
        e.company_email?.toLowerCase().includes(q) ||
        e.company_gst?.toLowerCase().includes(q) ||
        e.booth_no?.toLowerCase().includes(q) ||
        e.merchant_transaction_id?.toLowerCase().includes(q) ||
        e.noc_number?.toLowerCase().includes(q);
      return okFilter && okSearch;
    });
  }, [exhibitors, search, filter]);

  const allVisibleSelected = filtered.length > 0 && filtered.every(e => selected.has(e.merchant_transaction_id));

  const toggleOne = (txnId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filtered.forEach(e => next.delete(e.merchant_transaction_id));
      } else {
        filtered.forEach(e => next.add(e.merchant_transaction_id));
      }
      return next;
    });
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  // ── Send / resend for a single row ──────────────────────────────────────
  const sendOne = async (exhibitor) => {
    const isResend = exhibitor.noc_send_status === 'sent';
    setSendingIds(prev => new Set(prev).add(exhibitor.merchant_transaction_id));
    try {
      const { data } = await API.post('/noc/send', {
        merchantTransactionIds: [exhibitor.merchant_transaction_id],
      });
      const result = data.results?.[0];
      if (!result?.success) throw new Error(result?.error || 'Send failed');
      toast.success(`${isResend ? 'Resent' : 'Sent'} to ${exhibitor.company_name} ✅`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSendingIds(prev => {
        const next = new Set(prev);
        next.delete(exhibitor.merchant_transaction_id);
        return next;
      });
    }
  };

  // ── Bulk send to everyone selected ──────────────────────────────────────
  const sendSelected = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);

    if (!window.confirm(`Send NOC to ${ids.length} selected exhibitor${ids.length !== 1 ? 's' : ''}?`)) return;

    setBulkSending(true);
    try {
      const { data } = await API.post('/noc/send', { merchantTransactionIds: ids });
      if (!data.success) throw new Error(data.message);

      const failedResults = (data.results || []).filter(r => !r.success);
      if (data.failed > 0) {
        toast.error(`${data.sent} sent, ${data.failed} failed — see console for details`);
        console.error('NOC send failures:', failedResults);
      } else {
        toast.success(`Sent NOC to all ${data.sent} selected exhibitor${data.sent !== 1 ? 's' : ''} ✅`);
      }

      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setBulkSending(false);
    }
  };

  const s = stats || {};

  return (
    <Layout>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <FiFileText style={{ color:'#7c5cfc' }} /> NOC Dashboard
          </h1>
          <p className="page-subtitle">Send No Objection Certificates to exhibitors with a completed security deposit</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load} disabled={loading || bulkSending}>
          <FiRefreshCw style={{ marginRight:6 }} />{loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="page-body fade-in">

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:16, marginBottom:28 }}>
          <StatCard icon={<FiFileText />}    label="Eligible exhibitors" value={s.total ?? '—'}   color="#7c5cfc" />
          <StatCard icon={<FiCheckCircle />} label="NOC sent"            value={s.sent ?? '—'}    color="#00a86b" />
          <StatCard icon={<FiClock />}       label="Not sent yet"        value={s.pending ?? '—'} color="#d4900a" />
          <StatCard icon={<FiXCircle />}     label="Failed"              value={s.failed ?? '—'}  color="#c0392b" />
        </div>

        {/* Event filter */}
        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <label style={{ display:'flex', flexDirection:'column', gap:4, fontSize:12, fontWeight:700, color:'#4a4a5e', minWidth:280 }}>
            Event name
            <input
              value={eventName}
              onChange={e => setEventName(e.target.value)}
              onBlur={load}
              onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="e.g. People Matters TechHR India 2026"
              style={{ padding:'8px 12px', borderRadius:10, border:'1px solid #dcdce6', fontSize:13, outline:'none' }}
            />
          </label>
        </div>

        {/* Status filter + search */}
        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6 }}>
            {['ALL', 'sent', 'pending', 'failed'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border:'1px solid',
                borderColor: filter === f ? '#1a1a2e' : '#dcdce6',
                background:  filter === f ? '#1a1a2e' : '#fff',
                color:       filter === f ? '#fff'    : '#4a4a5e',
              }}>
                {f === 'ALL' ? 'All' : NOC_STATUS[f]?.label}
              </button>
            ))}
          </div>
          <div style={{ position:'relative', flex:1, minWidth:220 }}>
            <FiSearch style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#b0b0c0' }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search company, booth, GST, email, NOC no…"
              style={{ width:'100%', padding:'8px 12px 8px 34px', borderRadius:10, border:'1px solid #dcdce6', fontSize:13, outline:'none', boxSizing:'border-box' }}
            />
          </div>
        </div>

        {/* Bulk action bar — only shows once something's selected */}
        {selected.size > 0 && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap',
            background:'#1a1a2e', color:'#fff', borderRadius:12, padding:'12px 18px', marginBottom:16,
          }}>
            <span style={{ fontSize:13, fontWeight:600 }}>{selected.size} selected</span>
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={() => setSelected(new Set())}
                style={{ background:'transparent', border:'1px solid rgba(255,255,255,.3)', color:'#fff', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:600, cursor:'pointer' }}
              >
                Clear
              </button>
              <button
                onClick={sendSelected}
                disabled={bulkSending}
                style={{ background:'#00a86b', border:'none', color:'#fff', borderRadius:8, padding:'8px 16px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, opacity: bulkSending ? 0.6 : 1 }}
              >
                <FiSend />{bulkSending ? 'Sending…' : `Send NOC to Selected (${selected.size})`}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:48, color:'#b0b0c0', fontSize:14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:48, color:'#b0b0c0', fontSize:14 }}>
              {exhibitors.length === 0 ? 'No exhibitors with a completed deposit yet for this event.' : 'No results for this filter.'}
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#f8f8fc', borderBottom:'1px solid #ececf2' }}>
                    <th style={{ padding:'11px 14px', width:36 }}>
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} />
                    </th>
                    {['Company', 'Booth', 'GST', 'Phone / Email', 'Deposit', 'NOC No.', 'Status', 'Sent At', ''].map(h => (
                      <th key={h} style={{ padding:'11px 14px', textAlign:'left', fontWeight:700, color:'#6b6b80', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, i) => {
                    const statusKey = e.noc_send_status || 'pending';
                    const isSending = sendingIds.has(e.merchant_transaction_id);
                    const isResend = statusKey === 'sent';

                    return (
                      <tr key={e.merchant_transaction_id} style={{ borderBottom:'1px solid #f0f0f5', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding:'11px 14px' }}>
                          <input
                            type="checkbox"
                            checked={selected.has(e.merchant_transaction_id)}
                            onChange={() => toggleOne(e.merchant_transaction_id)}
                          />
                        </td>

                        <td style={{ padding:'11px 14px', maxWidth:180 }}>
                          <div style={{ fontWeight:700, color:'#1a1a2e' }}>{e.company_name}</div>
                        </td>

                        <td style={{ padding:'11px 14px' }}>
                          <span style={{ background:'#f1f1f6', borderRadius:8, padding:'3px 10px', fontWeight:700, fontSize:12 }}>
                            {e.booth_no || '—'}
                          </span>
                          {e.booth_name && <div style={{ fontSize:11, color:'#b0b0c0', marginTop:2 }}>{e.booth_name}</div>}
                        </td>

                        <td style={{ padding:'11px 14px', color:'#4a4a5e', fontFamily:'monospace', fontSize:12 }}>
                          {e.company_gst || '—'}
                        </td>

                        <td style={{ padding:'11px 14px' }}>
                          <div style={{ fontWeight:600, color:'#1a1a2e' }}>{e.company_phone || '—'}</div>
                          {e.company_email && <div style={{ fontSize:11, color:'#b0b0c0' }}>{e.company_email}</div>}
                        </td>

                        <td style={{ padding:'11px 14px', whiteSpace:'nowrap', fontWeight:800, color:'#1a1a2e' }}>
                          {fmt(e.deposit_amount)}
                        </td>

                        <td style={{ padding:'11px 14px' }}>
                          {e.noc_number ? (
                            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ fontFamily:'monospace', fontSize:12 }}>{e.noc_number}</span>
                              <button
                                title="Copy"
                                onClick={() => copy(e.noc_number)}
                                style={{ background:'none', border:'1px solid #dcdce6', borderRadius:6, padding:'2px 6px', cursor:'pointer', color:'#4a4a5e' }}
                              >
                                <FiCopy size={11} />
                              </button>
                            </span>
                          ) : <span style={{ color:'#dcdce6' }}>—</span>}
                        </td>

                        <td style={{ padding:'11px 14px' }}><NocChip status={statusKey} /></td>

                        <td style={{ padding:'11px 14px', whiteSpace:'nowrap', color:'#6b6b80', fontSize:12 }}>
                          {fmtDt(e.noc_sent_at)}
                        </td>

                        <td style={{ padding:'11px 14px' }}>
                          <button
                            onClick={() => sendOne(e)}
                            disabled={isSending || !e.company_email}
                            title={!e.company_email ? 'No email on file' : undefined}
                            className="btn btn-sm"
                            style={{
                              background: isResend ? '#7c5cfc' : '#00a86b',
                              color:'#fff', border:'none', whiteSpace:'nowrap',
                              opacity: isSending || !e.company_email ? 0.6 : 1,
                            }}
                          >
                            {isSending ? 'Sending…' : isResend ? 'Resend' : 'Send NOC'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p style={{ marginTop:12, fontSize:12, color:'#b0b0c0' }}>
          Showing {filtered.length} of {exhibitors.length} exhibitor{exhibitors.length !== 1 ? 's' : ''}
        </p>

      </div>
    </Layout>
  );
}